// PriceRangePicker.jsx
import React, { useState, useEffect, useRef } from "react";
import "../styles/global.css";

/**
 * Props:
 * - min (number) default 0
 * - max (number) default 1000
 * - step (number) default 1
 * - initialMin (number) default = min
 * - initialMax (number) default = max
 * - currency (string) default "₹" (or "$")
 * - onChange([min, max]) called during sliding/typing
 * - onAfterChange([min, max]) called when user finishes (mouseUp / blur)
 */
export default function PriceRangePicker({
  min = 0,
  max = 10000,
  step = 1,
  initialMin = min,
  initialMax = max,
  currency = "₹",
  onChange = () => { },
  onAfterChange = () => { },
}) {
  const [low, setLow] = useState(Math.max(min, Math.min(initialMin, initialMax)));
  const [high, setHigh] = useState(Math.min(max, Math.max(initialMax, initialMin)));

  // Keep refs to know when drag/interaction ends
  const dragging = useRef(false);
  const afterChangeTimeout = useRef(null);

  useEffect(() => {
    // notify on every update
    onChange([low, high]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [low, high]);

  // call onAfterChange after short delay when interaction ends
  const triggerAfterChange = (value) => {
    if (afterChangeTimeout.current) clearTimeout(afterChangeTimeout.current);
    afterChangeTimeout.current = setTimeout(() => {
      onAfterChange(value);
    }, 120); // small delay to wait for possible subsequent events
  };

  const handleLowChange = (val) => {
    const numeric = Number(val);
    // don't cross over high
    const newLow = Math.min(numeric, high - step);
    setLow(Math.max(min, newLow));
  };

  const handleHighChange = (val) => {
    const numeric = Number(val);
    // don't cross under low
    const newHigh = Math.max(numeric, low + step);
    setHigh(Math.min(max, newHigh));
  };

  // Handlers for range inputs
  const onLowInput = (e) => {
    dragging.current = true;
    handleLowChange(e.target.value);
  };
  const onHighInput = (e) => {
    dragging.current = true;
    handleHighChange(e.target.value);
  };

  const onLowMouseUp = () => {
    dragging.current = false;
    triggerAfterChange([low, high]);
  };
  const onHighMouseUp = () => {
    dragging.current = false;
    triggerAfterChange([low, high]);
  };

  // Numeric text inputs (for exact values)
  const onLowNumberBlur = (e) => {
    let v = Number(e.target.value || min);
    if (isNaN(v)) v = min;
    handleLowChange(v);
    triggerAfterChange([low, high]);
  };
  const onHighNumberBlur = (e) => {
    let v = Number(e.target.value || max);
    if (isNaN(v)) v = max;
    handleHighChange(v);
    triggerAfterChange([low, high]);
  };

  // derived percentage positions for styling the filled track
  const lowPct = ((low - min) / (max - min)) * 100;
  const highPct = ((high - min) / (max - min)) * 100;

  const format = (value) => `${currency}${Number(value).toLocaleString()}`;

  return (
    <div style={{ width: "100%", maxWidth: 520, padding: 8, fontFamily: "Inter, Arial, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 5 }}>
        <div style={{ fontSize: 14, color: "#333" }}>Price range</div>
        <div style={{ fontSize: 14, color: "#333" }}>
          {format(low)} — {format(high)}
        </div>
      </div>

      {/* Range sliders (stacked to allow two-thumb effect) */}
      <div style={{ position: "relative", height: 48 }}>
        {/* Base track */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 20,
            left: 0,
            right: 0,
            height: 6,
            borderRadius: 999,
            background: "#e6e6e6",
          }}
        />

        {/* Highlighted selected portion */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 20,
            left: `${lowPct}%`,
            width: `${Math.max(highPct - lowPct, 0)}%`,
            height: 6,
            borderRadius: 999,
            background: "linear-gradient(87.12deg, #238495 -117.62%, #1E7A8C -51.97%, #C38635 6.26%,#167A7A 69.43%, #3A2270 140.05%)",
          }}
        />

        {/* Low thumb */}
        <input
          type="range"
          className="prp-range-input"
          min={min}
          max={max}
          step={step}
          value={low}
          onChange={onLowInput}
          onMouseUp={onLowMouseUp}
          onTouchEnd={onLowMouseUp}
          aria-label="Minimum price"
          style={{
            position: "absolute",
            top: 12,
            height: 25,
            left: -4,
            right: 0,
            width: "100%",
            WebkitAppearance: "none",
            background: "transparent",
            /* NOTE: pointerEvents intentionally NOT set to "none" here.
               The whole input stays hit-testable so the browser can find
               its thumb on touch devices. The .prp-range-input CSS rule
               (global.css) disables clicks on the track but re-enables
               them on ::-webkit-slider-thumb / ::-moz-range-thumb, which
               is what actually makes only the thumb draggable. */
          }}
        />

        {/* High thumb */}
        <input
          type="range"
          className="prp-range-input"
          min={min}
          max={max}
          step={step}
          value={high}
          onChange={onHighInput}
          onMouseUp={onHighMouseUp}
          onTouchEnd={onHighMouseUp}
          aria-label="Maximum price"
          style={{
            position: "absolute",
            top: 10,
            left: 0,
            height: 25,
            right: -4,
            width: "100%",
            WebkitAppearance: "none",
            background: "transparent",
          }}
        />

        {/* Visible thumb handles (real clickable areas) */}
        <div
        // style={{
        //   position: "absolute",
        //   top: 10,
        //   left: `calc(${lowPct}% - 10px)`,
        //   width: 25,
        //   height: 25,
        //   borderRadius: 100,
        //   background: "linear-gradient(87.12deg, #238495 -117.62%, #1E7A8C -51.97%, #C38635 6.26%,#167A7A 69.43%, #3A2270 140.05%)",
        //   border: "4px solid #fff",
        //   boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        //   pointerEvents: "none",
        // }}
        />
        <div
        // style={{
        //   position: "absolute",
        //   top: 10,
        //   left: `calc(${highPct}% - 7%)`,
        //   width: 25,
        //   height: 25,
        //   borderRadius: 100,
        //   background: "linear-gradient(87.12deg, #238495 -117.62%, #1E7A8C -51.97%, #C38635 6.26%,#167A7A 69.43%, #3A2270 140.05%)",
        //   border: "4px solid #fff",
        //   boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        //   pointerEvents: "none",
        // }}
        />
      </div>

      {/* Numeric inputs and actions */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="number"
            value={low}
            min={min}
            max={max}
            step={step}
            onChange={(e) => handleLowChange(e.target.value)}
            onBlur={onLowNumberBlur}
            style={{ width: 70, padding: "6px 8px", borderRadius: 6, border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="number"
            value={high}
            min={min}
            max={max}
            step={step}
            onChange={(e) => handleHighChange(e.target.value)}
            onBlur={onHighNumberBlur}
            style={{ width: 70, padding: "6px 8px", borderRadius: 6, border: "1px solid #ccc" }}
          />
        </div>

        <button
          className="reset-btn"
          type="button"
          onClick={() => {
            setLow(min);
            setHigh(max);
            onAfterChange([min, max]);
          }}
          style={{
            // marginLeft: "auto",
            padding: "8px",
            borderRadius: 8,
            border: "none",
            background: "#f1f1f1",
            cursor: "pointer",
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}